import React from "react"
import { Anchor, Badge, Group, Paper, SimpleGrid, Stack, Text } from "@mantine/core"
import { CATALOG } from "../../docs/catalog"
import { GITHUB_URL } from "../helpers/constants"

// What this site is built from, on the page rather than in a commit message.
// Everything here comes from the docs bundle `bin/rails docs:sync` writes, so
// it cannot drift from the guides, the API reference or the agent directions
// the rest of the page is serving: if the panel says 0.3.0, the directions
// somebody copies below it are 0.3.0.

const formatDate = (value: string | null): string => {
  if (!value) return "unknown"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "unknown"
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric"
  })
}

// A date alone does not say whether a snapshot is stale, and the reader should
// not have to do the subtraction.
const relativeAge = (value: string | null): string | null => {
  if (!value) return null
  const then = new Date(value).getTime()
  if (Number.isNaN(then)) return null
  const days = Math.floor((Date.now() - then) / 86_400_000)
  if (days <= 0) return "today"
  if (days === 1) return "yesterday"
  if (days < 31) return `${days} days ago`
  const months = Math.floor(days / 30)
  return months === 1 ? "a month ago" : `${months} months ago`
}

// wide spans every column: the agent directions list is too long for one.
const Fact: React.FC<{ label: string; wide?: boolean; children: React.ReactNode }> = ({
  label,
  wide,
  children
}) => (
  <div style={wide ? { gridColumn: "1 / -1" } : undefined}>
    <Text size="xs" c="dimmed" tt="uppercase" fw={700} style={{ letterSpacing: "0.06em" }}>
      {label}
    </Text>
    <Text size="sm" mt={4}>
      {children}
    </Text>
  </div>
)

export const ReleaseInfo: React.FC = () => {
  const releaseUrl = `${GITHUB_URL}/releases/tag/v${CATALOG.sourceVersion}`
  const commitUrl = CATALOG.sourceCommit ? `${GITHUB_URL}/commit/${CATALOG.sourceCommit}` : null
  const syncedAge = relativeAge(CATALOG.generatedAt)

  return (
    <Paper className="or-release-info" radius="lg" p={{ base: "md", sm: "lg" }} withBorder>
      <Stack gap="md">
        <Group justify="space-between" align="center" wrap="wrap" gap="xs">
          <Group gap="xs">
            <Text fw={700}>OpenReceive {CATALOG.sourceVersion}</Text>
            <Badge variant="light" size="sm">
              current release
            </Badge>
          </Group>
          <Text size="sm" c="dimmed">
            Everything on this site — guides, API reference, agent directions — is generated from
            this release.
          </Text>
        </Group>

        <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }} spacing="lg" verticalSpacing="md">
          <Fact label="Version">
            <Anchor href={releaseUrl} target="_blank" rel="noopener noreferrer">
              v{CATALOG.sourceVersion}
            </Anchor>
            {commitUrl ? (
              <>
                {" · "}
                <Anchor href={commitUrl} target="_blank" rel="noopener noreferrer" c="dimmed">
                  {CATALOG.sourceCommit}
                </Anchor>
              </>
            ) : null}
          </Fact>

          <Fact label="Released">{formatDate(CATALOG.sourceDate)}</Fact>

          <Fact label="Docs updated">
            {formatDate(CATALOG.generatedAt)}
            {syncedAge ? (
              <Text span c="dimmed">
                {" "}
                ({syncedAge})
              </Text>
            ) : null}
          </Fact>

          <Fact label="Published">
            {CATALOG.guideCount} guides · {CATALOG.apiEntryCount} API entries
          </Fact>

          <Fact label="For agents">
            <Anchor href={CATALOG.llms.index} target="_blank" rel="noopener noreferrer">
              llms.txt
            </Anchor>
            {" · "}
            <Anchor href={CATALOG.llms.full} target="_blank" rel="noopener noreferrer">
              llms-full.txt
            </Anchor>
          </Fact>

          <Fact label="Source">
            <Anchor href={GITHUB_URL} target="_blank" rel="noopener noreferrer">
              GitHub
            </Anchor>
            {" · "}
            <Anchor href={`${GITHUB_URL}/blob/master/CHANGELOG.md`} target="_blank" rel="noopener noreferrer">
              Changelog
            </Anchor>
          </Fact>

          {/* Last and full width, each file kept whole: a line breaks only
              between files, never between a name and its size. */}
          <Fact label="Agent directions" wide>
            {CATALOG.agentDirections.map((payload, index) => (
              <React.Fragment key={payload.path}>
                <span style={{ whiteSpace: "nowrap" }}>
                  <Anchor href={payload.path} target="_blank" rel="noopener noreferrer">
                    {payload.stack}.md
                  </Anchor>{" "}
                  <Text span c="dimmed">
                    {(payload.bytes / 1024).toFixed(1)} KB
                  </Text>
                  {index < CATALOG.agentDirections.length - 1 ? " ·" : null}
                </span>{" "}
              </React.Fragment>
            ))}
          </Fact>
        </SimpleGrid>
      </Stack>
    </Paper>
  )
}
